/**
 * Resolved, reactive operational configuration for the app.
 *
 * Joins the two layers Melodimals actually has — the build environment
 * (platform) and the adult's settings behind the parental gate
 * (`localStorage`) — over the PURE resolver in `@melodimals/config/app-config`.
 * Nothing is decided here: it only reads `import.meta.env`, reads storage, and
 * exposes the result.
 *
 * Changing a setting re-resolves the entire configuration and propagates it through
 * a signal: anything that depends on it knows **without reloading** (which is half
 * the meaning of "configuration over code").
 */

import { createContext, createSignal, type ParentComponent, useContext } from "solid-js";
import {
  type AppConfig,
  type ParentSettings,
  readParentSettings,
  resolveAppConfig,
  writeParentSettings,
} from "@melodimals/config/app-config";

interface ConfigContextValue {
  /** Effective configuration (defaults → build env → adult settings). */
  config: () => AppConfig;
  /** Only what the adult changed (for rendering the controls). */
  settings: () => ParentSettings;
  /** Applies an adult change and persists it on this device. */
  update: (patch: ParentSettings) => void;
  /** Returns control to the build (clears this device's settings). */
  reset: () => void;
}

const ConfigCtx = createContext<ConfigContextValue>();

export function useAppConfig(): ConfigContextValue {
  const ctx = useContext(ConfigCtx);
  if (!ctx) {
    throw new Error("useAppConfig must be used inside <ConfigProvider>");
  }
  return ctx;
}

/** Vite's `import.meta.env`; absent in pure Node tests. */
function buildEnv(): Record<string, string | undefined> {
  const meta: ImportMeta & { env?: Record<string, string | undefined> } = import.meta;
  return meta.env ?? {};
}

function storage(): Storage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    // Cookies/storage blocked: continue with build defaults.
    return undefined;
  }
}

export const ConfigProvider: ParentComponent = (props) => {
  // Initial read in a constant: reading the signal here would be a use outside
  // the reactive scope (and the lint is right — it would never run again).
  const inicial = readParentSettings(storage());
  const [settings, setSettings] = createSignal<ParentSettings>(inicial);
  const [config, setConfig] = createSignal<AppConfig>(resolveAppConfig(buildEnv(), inicial));

  const apply = (next: ParentSettings) => {
    setSettings(next);
    setConfig(resolveAppConfig(buildEnv(), next));
    writeParentSettings(storage(), next);
  };

  const value: ConfigContextValue = {
    config,
    settings,
    update: (patch) => {
      apply({ ...settings(), ...patch });
    },
    reset: () => {
      apply({});
    },
  };

  return <ConfigCtx.Provider value={value}>{props.children}</ConfigCtx.Provider>;
};
